import {ComponentType, LazyExoticComponent, Suspense, useContext, useEffect, useState} from 'react';
import Root from 'react-shadow/emotion';
import {CacheProvider} from '@emotion/react';
import createCache from '@emotion/cache';
import {useQuery} from '@tanstack/react-query';
import {dom} from '@fortawesome/fontawesome-svg-core';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import Loading from '@common/Loading';
import Banner, {BannerTypes} from 'patient-ping-remedy/packages/banner';
import {GENERICERROR} from '@manual_encounters/constants/BannerMessages';
import {addMinutes, differenceInMilliseconds} from 'date-fns';

function getThothTokenExpiration(token?: string): number | false {
  try {
    if (!token) return false;
    const payload = JSON.parse(atob(token.split('.')[1]));
    // Refresh if less than 5 minutes remaining
    return differenceInMilliseconds(new Date(payload.exp * 1000), addMinutes(new Date(), 5));
  } catch {
    return false;
  }
}

type Props = {
  remote: string;
  component: LazyExoticComponent<ComponentType<{ [key: string]: unknown }>>;
  [key: string]: unknown;
}

export default function RemoteComponent({ remote, component: Component, ...props }: Props) {
  const [shadow, setShadow] = useState<HTMLElement | null>(null);
  const [cache, setCache] = useState<ReturnType<typeof createCache> | null>(null);
  const { carecoApi } = useContext(CarecoApiContext);

  const { data: accessToken, error } = useQuery({
    queryKey: ['thoth', 'token'],
    queryFn: () => carecoApi?.getThothToken(),
    refetchInterval: ({ state }) => getThothTokenExpiration(state.data),
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  useEffect(() => {
    if (shadow?.shadowRoot && !cache) {
      setCache(createCache({
        key: remote,
        container: shadow.shadowRoot,
      }));
    }
  }, [shadow, cache]);

  return (
    <Root.div ref={setShadow}>
      <style>{dom.css()}</style>
      {error ? (
        <Banner type={BannerTypes.PATIENT_SAFETY}>
          Failed to call remote API. {GENERICERROR}
        </Banner>
      ) : shadow && cache && accessToken ? (
        <CacheProvider value={cache}>
          <Suspense fallback={<Loading />}>
            <Component accessToken={accessToken} {...props} />
          </Suspense>
        </CacheProvider>
      ) : (
        <Loading />
      )}
    </Root.div>
  );
}
